import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Box, debounce, Stack, Typography } from "@mui/material";
import { AsyncContent, RoleComponentGuard, Search } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { useActionHook, useNavigationHooks, useUrlHooks } from "hooks";
import { useNavigate } from "hooks/shared-hooks/use-navigate";
import { useClientActions } from "store";
import { UrlQueryParams } from "store/types";
import { IClient, IClientFilters } from "store/types/user-management";

import EmptyCardClients from "../../../assets/images/EmptyCardList.png";
import { RoleName } from "../../../constants";
import { ClientDrawer } from "./Drawer/ClientDrawer";
import { Filters } from "./Filters";
import { Table } from "./Table/Table";

// TODO(shared): move this copy into the shared EMPTY_CARD_CONTENT constant
// (src/constants/empty-card.ts) under a `clientManage` key once that file is
// updated by the owning work. Inlined here because EMPTY_CARD_CONTENT does not
// yet expose a clients-list empty state and that constant is out of scope.
const CLIENTS_EMPTY_CARD_CONTENT = {
  title: "Your clients will appear here",
  description: "Add a client to start managing their entities, workspace, and reporting in one place.",
  externalDescription: "Reach out to your Altius representative to add clients."
} as const;

const emptyTitleSx = { fontSize: "18px", fontWeight: 500, color: "#0a3142", mb: "4px" } as const;
const emptySubtitleSx = { fontSize: "14px", color: "#898989", m: 0 } as const;

export const Clients: React.FC = () => {
  const { getClientsAction } = useClientActions();
  const { data, loading, error, action } = useActionHook(getClientsAction);
  const { queryParams } = useUrlHooks();
  const { removeParams } = useNavigationHooks();

  const [queries, setQueries] = useState<IClientFilters>({ page: 1, per_page: 50 });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editClient, setEditClient] = useState<IClient | null>(null);
  const isInitialLoadStarted = useRef(false);
  const navigate = useNavigate();

  const updateQueries = useCallback((data: Partial<IClientFilters>) => {
    setQueries((prev) => ({ ...prev, ...data, page: 1 }));
  }, []);

  const onSearch = useCallback(
    (search: string) => {
      updateQueries({ search, page: 1 });
    },
    [updateQueries]
  );

  // Stabilize the debounced handler so it retains its internal timer across
  // renders (a fresh `debounce(...)` each render would reset the debounce state).
  const onSearchDebounce = useMemo(() => debounce(onSearch, 500), [onSearch]);
  useEffect(() => () => onSearchDebounce.clear(), [onSearchDebounce]);

  const handleChangePage = useCallback((newPage: number) => {
    setQueries((prev) => ({ ...prev, page: newPage + 1 }));
  }, []);

  useEffect(() => {
    action(queries);
  }, [queries, action]);

  // Open the Add Client modal when triggered from the global header Add menu
  // via the `clientManagement=create` query param. Optional chaining keeps the
  // effect safe when `queryParams` is temporarily undefined during navigation.
  useEffect(() => {
    if (queryParams?.clientManagement === "create") {
      setIsAddModalOpen(true);
      // NOTE: `clientManagement` is a real runtime query param (set by the header
      // Add menu) but is not yet declared on the shared UrlQueryParams type, which
      // is out of scope to edit. Assert the key so removeParams accepts it.
      removeParams(["clientManagement" as keyof UrlQueryParams]);
    }
  }, [queryParams, queryParams?.clientManagement, removeParams]);

  const refetchTable = useCallback(() => {
    action(queries);
  }, [action, queries]);

  useEffect(() => {
    if (loading) {
      isInitialLoadStarted.current = true;
    }
  }, [loading]);

  const clientsData = data;
  const totalClients = data?.total ?? 0;
  const hasActiveFilters = useMemo(
    () => Boolean(queries.search || queries.type || queries.rm),
    [queries.search, queries.type, queries.rm]
  );
  // Compute memoized render-state flags, accurately recalculated each render
  // from the current shape of the client query and the `isInitialLoadStarted`
  // ref. Without these guards, fresh successful loads would be misclassified
  // as "empty" on the very first render.
  const isError = Boolean(error);
  const hasLoadedOnce = isInitialLoadStarted.current || Boolean(clientsData);
  const isLoadedWithData = hasLoadedOnce && !loading && totalClients > 0;
  const isFilteredEmpty = hasLoadedOnce && !loading && totalClients === 0 && hasActiveFilters;
  // The dedicated empty-state card is reserved for "no clients ever created"
  // (no filters active). When filters are active but no rows match, we keep
  // the head (Filters + Search) mounted so the user can adjust them and see a
  // filtered-empty message inside the table region.
  const showEmptyCard = hasLoadedOnce && !loading && totalClients === 0 && !hasActiveFilters;

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      {showEmptyCard && !isError ? (
        <EmptyCard
          title={CLIENTS_EMPTY_CARD_CONTENT.title}
          description={{
            text: CLIENTS_EMPTY_CARD_CONTENT.description,
            externalText: CLIENTS_EMPTY_CARD_CONTENT.externalDescription
          }}
          image={{
            src: EmptyCardClients,
            sx: { width: 315, height: 125 }
          }}
          fitPage
        />
      ) : (
        <Stack
          sx={{
            bgcolor: "white",
            boxShadow: "0px 1px 2px rgba(18, 18, 23, 0.1)",
            borderRadius: "8px",
            p: "16px",
            gap: "16px",
            flexGrow: 1,
            overflow: "hidden",
            height: "100%",
            width: "100%"
          }}
        >
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Stack direction="row" sx={{ alignItems: "center", gap: "10px" }}>
              <Filters
                queries={queries}
                onChange={(partial) => updateQueries(partial)}
                onFilterToggle={() => undefined}
              />
            </Stack>
            <Stack direction="row" sx={{ alignItems: "center", gap: "18px" }}>
              <Box sx={{ "& input": { height: "30px" }, "& input::placeholder": { fontSize: "16px" } }}>
                <Search
                  defaultValue={queries.search || ""}
                  onChange={(e) => onSearchDebounce(e.target.value)}
                  placeholder="Search"
                />
              </Box>
            </Stack>
          </Stack>
          <AsyncContent spinnerProps={{ size: 150 }} loading={loading}>
            {isError ? (
              <Stack sx={{ flexGrow: 1 }} alignItems="center" justifyContent="center">
                <Typography sx={emptyTitleSx}>Couldn&apos;t load clients.</Typography>
              </Stack>
            ) : isFilteredEmpty ? (
              <Stack sx={{ flexGrow: 1 }} alignItems="center" justifyContent="center">
                <Typography sx={emptyTitleSx}>No clients match your filters.</Typography>
                <Typography sx={emptySubtitleSx}>Try adjusting your search or filters.</Typography>
              </Stack>
            ) : isLoadedWithData && clientsData ? (
              <Table
                refetchTable={refetchTable}
                data={clientsData}
                handleChangePage={handleChangePage}
                rowsPerPage={queries.per_page!}
                page={queries.page!}
                onEditClient={setEditClient}
              />
            ) : null}
          </AsyncContent>
        </Stack>
      )}

      <ClientDrawer
        open={isAddModalOpen || Boolean(editClient)}
        client={editClient}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditClient(null);
        }}
        onSuccess={() => refetchTable()}
      />
    </RoleComponentGuard>
  );
};
